import { useEffect, useState } from 'react'
import styled, { useTheme } from 'styled-components'
import { Radio, RadioButton } from '@/components/common/inputs/RadioButton'
import { Typo } from '@/components/common/typography'
import { NewCouponCard } from '@/components/domains/coupon/new-coupon-card'
import ModalSliderDefault from '@/components/domains/order2/modal-slider-default'
import { GlobalColorEnum } from '@/constants/global-color.enum'
import { UseModalProps } from '@/containers/hooks/useModal'
import { getAmountText, getBenefitTextNew, getDatePeriodTextNew } from '@/utils/coupon.util'
import { currencyText } from '@/utils/utilNumber'
import type { CouponForEstimateOrderScheme } from '@/apis/rest/generated/types'

interface ModalOrderProductCouponSelectProps {
  title: string
  useModalProps: UseModalProps<unknown>
  appliedCoupon: CouponForEstimateOrderScheme | undefined
  couponList: CouponForEstimateOrderScheme[]
  onSelectCoupon: (couponId: number | undefined) => void
}

export const ModalOrderProductCouponSelect = ({
  title,
  useModalProps,
  appliedCoupon,
  couponList = [],
  onSelectCoupon,
}: ModalOrderProductCouponSelectProps) => {
  const { color } = useTheme()

  const [selectedCoupon, setSelectedCoupon] = useState<CouponForEstimateOrderScheme | undefined>(undefined)

  const handleCouponSelect = ({ couponId, isAvailable }: { couponId: number | undefined; isAvailable: boolean }) => {
    if (!isAvailable) {
      return
    }
    const selectedCoupon = couponList.find((coupon) => coupon.id === couponId)
    setSelectedCoupon(selectedCoupon)
  }

  const handleCloseModal = () => {
    setSelectedCoupon(appliedCoupon)
  }

  const handleClickApplyButton = () => {
    onSelectCoupon(selectedCoupon?.id)
    setSelectedCoupon(undefined)
    useModalProps.hideModal()
  }

  useEffect(() => {
    setSelectedCoupon(appliedCoupon)
  }, [appliedCoupon])

  return (
    <ModalSliderDefault
      title={title}
      centerTitle
      useModalProps={useModalProps}
      titleNoBorder
      onCancel={handleCloseModal}
    >
      <StyledCouponListContainer count={couponList.length}>
        <Wrapper>
          <StyledNotice>
            <Typo variant="Body3 Regular" color={color.gray500}>
              {'쿠폰 추가는 마이페이지>쿠폰함에서 가능해요'}
            </Typo>
          </StyledNotice>
          <StyledCouponList>
            <StyledCoupon
              selected={!selectedCoupon}
              disabled={false}
              onClick={() => handleCouponSelect({ couponId: undefined, isAvailable: true })}
            >
              <Typo variant="Body2 Bold">쿠폰 적용 안함</Typo>
              <Radio type="radio" checked={!selectedCoupon} variant={'medium'} readOnly />
            </StyledCoupon>
            {couponList?.map(
              ({
                id,
                name,
                isMembership,
                isAvailable,
                startedAt,
                expiredAt,
                canConjunctUse,
                discountType,
                discountRate,
                issueType,
                minOrderAmount,
                maxDiscountAmount,
                hasUsingPeriod,
                hasSpecifiedCoverage,
                coverage,
                appliedDiscountAmount,
                discountAmount,
              }) => {
                const couponTitle = getBenefitTextNew({
                  appliedDiscountAmount,
                  discountAmount,
                  discountType,
                  discountRate,
                  issueType,
                  name,
                  isAvailable,
                })
                const amountText = getAmountText({
                  hasMinOrderAmount: minOrderAmount > 0,
                  minOrderAmount,
                  maxDiscountAmount,
                })
                const datePeriodText = getDatePeriodTextNew({
                  hasUsingPeriod,
                  startedAt: new Date(startedAt),
                  expiredAt: new Date(expiredAt),
                })
                const isSelected = selectedCoupon?.id === id

                return (
                  <StyledCoupon
                    key={id}
                    selected={isSelected}
                    disabled={!isAvailable}
                    onClick={() => handleCouponSelect({ couponId: id, isAvailable })}
                  >
                    <NewCouponCard
                      title={couponTitle}
                      name={name}
                      amountText={amountText}
                      datePeriodText={datePeriodText}
                      isMembership={isMembership}
                      isConjunctUse={canConjunctUse}
                      disabled={!isAvailable}
                      hasSpecifiedCoverage={hasSpecifiedCoverage}
                      coverage={coverage}
                    />
                    <RadioButton
                      id={id.toString()}
                      variant="medium"
                      checked={isSelected}
                      disabled={!isAvailable}
                      onChange={() => handleCouponSelect({ couponId: id, isAvailable })}
                    />
                  </StyledCoupon>
                )
              }
            )}
          </StyledCouponList>
        </Wrapper>
      </StyledCouponListContainer>
      <StyledDivider />
      <ApplyCouponButtonWrapper>
        <ApplyCouponButton onClick={handleClickApplyButton}>
          <Typo variant="Body1 Bold" color={color.grayWhite}>
            {!!selectedCoupon
              ? `${currencyText(selectedCoupon.appliedDiscountAmount)} 할인 적용하기`
              : '쿠폰 적용하지 않기'}
          </Typo>
        </ApplyCouponButton>
      </ApplyCouponButtonWrapper>
    </ModalSliderDefault>
  )
}

const StyledCouponListContainer = styled.div<{ count: number }>`
  position: relative;
  padding: 1rem 1.6rem 0;
  max-height: calc(100vh * 0.9);
  height: ${({ count }) => (count >= 3 ? `calc(100vh * 0.6)` : ``)};
`

const StyledNotice = styled.div`
  background-color: ${(props) => props.theme.color.gray50};
  padding: 1.2rem 1.6rem;
  border-radius: 0.8rem;
`

const StyledCouponList = styled.div`
  margin: 1.6rem 0;
  display: flex;
  flex-direction: column;
  gap: 1.2rem;
`

const StyledCoupon = styled.div<{ selected: boolean; disabled: boolean }>`
  padding: 1.6rem;
  display: flex;
  justify-content: space-between;
  align-items: center;

  border: ${({ theme, selected }) =>
    selected ? `1px solid ${theme.color.blue500}` : `1px solid ${theme.color.gray100}`};
  box-sizing: border-box;
  border-radius: 0.8rem;
  background-color: ${(props) => props.theme.color.grayWhite};
  cursor: pointer;

  &[disabled] {
    border-color: ${({ theme }) => theme.color.gray100};
  }
`

const Wrapper = styled.div`
  overflow-y: scroll;
  //height: calc(100% - 20px);
  height: 100%;
  padding-bottom: 1.6rem;
`

const StyledDivider = styled.div`
  height: 0.1rem;
  background-color: ${GlobalColorEnum.Gray70};
`

const ApplyCouponButtonWrapper = styled.div`
  padding: 1.6rem;
  margin-bottom: calc(env(safe-area-inset-bottom));
`

const ApplyCouponButton = styled.button`
  width: 100%;
  padding: 1.4rem 1.6rem;
  background-color: ${({ theme }) => theme.color.blue500};
  border-radius: 0.8rem;
  border: none;
`
